/**
 * レビュー画面の材料表から原料と歩留まりを登録する(#222)。
 *
 * 記録は「マニュアルを編集」(#216)と同じ manualEdits(target=ingredient, key=材料名,
 * field=raw_materials)で、どちらの画面で登録しても同じ記録になる。本文へは
 * 「回答を本文に反映」で resolve が当てる(画面は依頼を記録するだけ)。
 *
 * moveFrom を渡すと「この材料を他の材料の原料にする」(#226)になる。移す材料を原料の行に
 * 入れた状態で開き、保存すると原料の登録と移す材料の削除依頼(#194)を1回の書き込みで記録する。
 * 片方だけ残ると材料表と原料に二重に載るため、バッチで一緒に書く。
 */
import { useState } from "react";
import { serverTimestamp, writeBatch } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../auth";
import { useI18n } from "../lib/i18n";
import { editDocId, type EditDoc } from "../lib/manual-edits";
import { ingredientEditKey } from "../lib/review";
import {
  draftOf,
  hasInvalidRawMaterial,
  normalizeRawMaterials,
  rowsOf,
  serializeRawMaterials,
  type RawMaterial,
} from "../lib/raw-materials";
import { RawMaterialRows } from "./RawMaterialRows";

interface Props {
  dishId: string;
  ingredient: {
    name: string;
    quantity: number | null;
    unit: string | null;
    raw_materials?: RawMaterial[];
  };
  /** 記録済みの編集(manualEdits 全件) */
  edits: Record<string, EditDoc>;
  /** 他の材料の原料へ移す材料(#226)。保存時に材料表からの削除依頼も記録する */
  moveFrom?: { name: string; quantity: number | null; unit: string | null; note: string | null };
  onClose: () => void;
}

export function RawMaterialsPanel({ dishId, ingredient, edits, moveFrom, onClose }: Props) {
  const { user, paths } = useAuth();
  const { t } = useI18n();
  const id = editDocId("ingredient", ingredient.name, "raw_materials");
  // 本文の値(反映済み)。保存値がこれと同じなら記録を消す(差分だけを持つ。#203 と同じ)
  const current = serializeRawMaterials(ingredient.raw_materials);
  const initial = edits[id]?.value ?? current;
  const [draft, setDraft] = useState<string>(() => {
    const rows = rowsOf(initial);
    if (moveFrom) {
      // 移す材料を原料の行に入れる(同名が既にあれば足さない)
      const exists = rows.some((r) => r.name.trim() === moveFrom.name.trim());
      return draftOf(
        exists
          ? rows
          : [
              ...rows,
              {
                name: moveFrom.name,
                quantity: moveFrom.quantity != null ? String(moveFrom.quantity) : "",
                unit: moveFrom.unit ?? "",
                yield_pct: "",
                note: moveFrom.note ?? "",
              },
            ],
      );
    }
    // 未登録なら空の1行から始める(開いてすぐ書けるように)
    return draftOf(rows.length ? rows : [{ name: "", quantity: "", unit: "", yield_pct: "", note: "" }]);
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const value = normalizeRawMaterials(draft);
  // 移すときは原料が同じでも削除依頼を書くので、常に保存できる
  const dirty = value !== initial || !!moveFrom;
  // 読めない歩留まり・量は保存すると黙って空になるので、直すまで保存させない(#238)
  const invalid = hasInvalidRawMaterial(draft);

  async function save() {
    if (!user || !paths || invalid) return;
    setSaving(true);
    setError(null);
    try {
      const batch = writeBatch(db);
      const ref = paths.manualEditDoc(dishId, id);
      if (value === current) batch.delete(ref);
      else
        batch.set(ref, {
          target: "ingredient",
          key: ingredient.name,
          field: "raw_materials",
          value,
          editedBy: user.uid,
          editedAt: serverTimestamp(),
        });
      if (moveFrom) {
        batch.set(paths.ingredientEditDoc(dishId, ingredientEditKey("remove", moveFrom.name)), {
          op: "remove",
          name: moveFrom.name,
          requestedBy: user.uid,
          requestedAt: serverTimestamp(),
        });
      }
      await batch.commit();
      onClose();
    } catch (e) {
      setError(`${t("保存に失敗しました:")} ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="raw-materials raw-panel">
      {moveFrom && (
        <p className="small">
          <strong>{t("「{from}」を「{to}」の原料にします。", { from: moveFrom.name, to: ingredient.name })}</strong>{" "}
          {t("保存すると「{from}」は材料表から削除され（反映時）、「{to}」の原料として残ります。歩留まりを入れると必要量の目安が出ます。", { from: moveFrom.name, to: ingredient.name })}
        </p>
      )}
      <p className="muted small">
        {t("「{name}」を作るのに使う原料を登録します。原料の量を空けておくと、分量と歩留まりから必要量の目安を出します。反映は品目の画面の「回答を本文に反映」です。", { name: ingredient.name })}
      </p>
      <RawMaterialRows
        rows={rowsOf(draft)}
        parent={{ quantity: ingredient.quantity, unit: ingredient.unit }}
        onChange={(rows) => setDraft(draftOf(rows))}
      />
      {error && <p className="error">{error}</p>}
      <div className="step-edit-actions">
        <button onClick={() => void save()} disabled={saving || !dirty || invalid}>
          {saving ? t("保存中…") : moveFrom ? t("原料にして保存") : t("原料を保存")}
        </button>
        <button className="link" onClick={onClose} disabled={saving}>
          {dirty ? t("保存せずに閉じる") : t("閉じる")}
        </button>
      </div>
    </div>
  );
}
